<template>
  <interview-layout>
    <div>
      <div class="d-flex flex-column flex-lg-row justify-content-center">
        <div class="container-narrow w-100">
          <h1 class="px-4 fade-in-up-enter-active animation-slowest max-100 text-primary">
            Subsidieresultaat
          </h1>
          <div
            class="fade-in-up-enter-active animation-slowest delay-400"
            inner-class="small"
            color="alternate"
            :outer-padding="true"
          >
            <base-card inner-class="container-narrow w-100" color="alternate" :outer-padding="true">
            <h6 class="mb-2">Voor welke regelingen komt u in aanmerking?</h6>  
            <base-card class="mb-4" inner-class="small" color="secondary">
                <checkmark-item
                  v-for="outcome in visibleSubsidyOutcomes"
                  :key="outcome.subsidy"
                  class="mb-2"
                  :icon="outcome.isEligible ? 'check-circle-fill' : 'x-circle-fill'"
                  :icon-color="outcome.isEligible ? 'success' : 'danger'"
                >
                  <strong class="fw-600">
                    {{ outcome.subsidy }}
                    <span v-if="outcome.twk?.isEligible" class="fw-normal"> (TWK mogelijk)</span>
                  </strong>
                  <div v-if="outcome.isEligible" class="mt-1">
                    <template v-if="outcome.twk?.isEligible">
                      U komt in aanmerking voor deze subsidie via TWK. {{ outcome.amountText }}.
                    </template>
                    <template v-else>
                      Mogelijk komt u in aanmerking voor deze subsidie. {{ outcome.amountText }}.
                    </template>
                  </div>
                  <div v-else class="mt-1">
                    {{ getSubsidyFailureMessage(outcome) }}
                  </div>
                </checkmark-item>
                <p v-if="visibleSubsidyOutcomes.length === 0" class="mb-0">
                  Er zijn op dit moment geen aanvullende regelingen om te tonen.
                </p>
              </base-card>
              <base-input
                v-if="showHelpQuestion"
                type="radiobuttons"
                :options="yesNo"
                name="interestedInOtherSubsidies"
                v-model:value="lead.otherSubsidies.interestedInOtherSubsidies"
              >
                Wilt u hulp bij het aanvragen van deze aanvullende subsidies?
              </base-input>
            </base-card>
          </div>
        </div>
      </div>

      <button-row class="mt-4 flex-row-reverse">
        <base-button class="ms-auto" icon="bi-arrow-right" type="submit" @click="onSubmit">Volgende</base-button>
        <base-button variant="minimal-secondary" icon="bi-arrow-left" icon-position="start" @click="onPrev">Terug</base-button>
      </button-row>
    </div>
  </interview-layout>
</template>

<script>
import InterviewLayout from "@/views/layouts/InterviewLayout.vue";
import BaseCard from "@/components/BaseCard.vue";
import BaseButton from "@/components/BaseButton.vue";
import BaseInput from "@/components/BaseInput.vue";
import ButtonRow from "@/components/ButtonRow.vue";
import CheckmarkItem from "@/components/CheckmarkItem.vue";
import { CommonComposable } from '@/helpers/CommonComposable';
import { useAllStores } from "@/helpers/UseAllStores";
import { FlowService } from "@/services/FlowService";
import { MunicipalitySubsidyService } from '@/services/MunicipalitySubsidyService';

export default {
  components: {
    InterviewLayout,
    BaseCard,
    BaseButton,
    BaseInput,
    ButtonRow,
    CheckmarkItem,
  },
  setup(props, context) {
    const stores = useAllStores();

    return { ...CommonComposable(props, context), ...stores };
  },

  data() {
    return {
      yesNo: [
        { label: "Ja", value: true },
        { label: "Nee", value: false },
      ],
    };
  },

  computed: {
    showHelpQuestion() {
      return !FlowService.hasSubsidyFollowUpSteps() &&
        this.visibleSubsidyOutcomes.some((outcome) => outcome.isEligible);
    },
    startingPossibleSubsidies() {
      if (!this.lead || !this.lead.address) return [];
      return MunicipalitySubsidyService.getEligibleSubsidies(this.lead, 'financing');
    },

    subsidyOutcomes() {
      if (!this.lead || !this.lead.address) return [];
      return MunicipalitySubsidyService.getPostAddressSubsidyOutcomes(this.lead, 'final');
    },

    visibleSubsidyOutcomes() {
      const eligibleAtStart = new Set(this.startingPossibleSubsidies);

      return this.subsidyOutcomes.filter((outcome) => {
        if (outcome.isEligible) {
          return true;
        }

        return eligibleAtStart.has(outcome.subsidy);
      });
    },


    hasSubsidyFailureMessage() {
      return !!this.subsidyFailureMessage;
    }
  },

  methods: {
    syncSubsidyEligibility() {
      if (!this.lead?.otherSubsidies) return;

      const eligibleSubsidies = this.visibleSubsidyOutcomes
        .filter((outcome) => outcome.isEligible && !outcome.twk?.isEligible)
        .map((outcome) => String(outcome.subsidy || '').trim())
        .filter(Boolean);

      this.lead.otherSubsidies.twkEligibleSubsidies = this.visibleSubsidyOutcomes
        .filter((outcome) => outcome.twk?.isEligible)
        .map((outcome) => String(outcome.subsidy || '').trim())
        .filter(Boolean);

      this.lead.otherSubsidies.subsidyEligibility = [...new Set(eligibleSubsidies)];
    },

    getSubsidyFailureMessage(outcome) {
      const failedRule = outcome?.failedRule;

      if (failedRule === "executionEligible") {
        return `U komt niet in aanmerking voor deze subsidie, omdat ${outcome.reason}.`;
      }

      if (failedRule === "vhfVeendamIncomeAtMost180") {
        return "U komt niet in aanmerking voor deze regeling, omdat het subsidiepercentage bij dit inkomen op 0% uitkomt.";
      }

      if (outcome?.reason) {
        return `U komt niet in aanmerking, omdat ${outcome.reason}.`;
      }

      return "U komt niet in aanmerking voor deze subsidie.";
    },

    onSubmit() {
      FlowService.pushNext(this);
    },

    onPrev() {
      FlowService.pushPrev(this);
    },
  },
  watch: {
    visibleSubsidyOutcomes: {
      handler() {
        this.syncSubsidyEligibility();
      },
      immediate: true,
      deep: true,
    },
  },
};
</script>

<style scoped lang="scss">

</style>
